import React, { useRef, useEffect } from "react";
import { styled } from "../../../../../stitches.config";
import { h1, h3, bodyLarge, bodySmall } from "../../../../../redesign/commonStyles/Fonts";
import { Field, FormikProps, ErrorMessage } from "formik";
import { CSSTransition } from "react-transition-group";
import { observer } from "mobx-react";

import BridDonationProgress from "../../../../Article/BridDonationProgress"; // "./Article/BridDonationProgress";
import PaymentSuccess from "./PaymentSuccess";
import BridDonate from "../../../BridDonate";
import { IFormValues } from "../../../../ArticleBrid";
import { useStores } from "../../../../../helpers/useStores";
import { useWindowWidth } from "../../../../../helpers/useWindowWidth";

import { ReactComponent as BridTick } from "../../../../../assets/img/brid_correct.svg?react";
import { ReactComponent as PersonIcon } from "../../../../../assets/img/brid_person.svg?react";
import { ReactComponent as BridCheckmark } from "../../../../../assets/img/brid_checkmark.svg?react";
import { ReactComponent as PadlogIcon } from "../../../../../assets/img/padlock.svg?react";

import ArrowIcons from "../../../../../assets/img/left.svg";
import ActiveCheckbox from "../../../../../assets/img/activeCheckbox.svg";
import BridLogo from "../../../../../assets/img/by_naTemat_logo_Brid.svg";
import BridLogoOrange from "../../../../../assets/img/logo_pomaranczowe_brid.svg";
import przelewy24 from "../../../../../assets/img/logo-przelewy-24.svg";
import ArrowLeftImage from "../../../../../assets/img/arrowLeft.svg";

import Media from "../../../../common/Media";
import { useLocation, useNavigate } from "react-router-dom";
import { useThemeContext } from "../../../../../redesign/components/StitchesThemeProvider";

interface IProps {
  setShowModal: (value: boolean) => void;
  waiting: boolean;
  payForm?: any;
}

const Payment: React.FC<
  IProps & FormikProps<IFormValues>
> = observer((props) => {

  const location = useLocation();
  const navigate = useNavigate();
  const { values } = props;
  const { ArticleStore } = useStores();
  const width = useWindowWidth();
  const ref = useRef<HTMLDivElement>(null);

  const { isDarkMode } = useThemeContext();

  useEffect(() => {
    ref.current?.scrollTo(0, 0);
  }, [location.search]);

  const replaceCommas = (value: string) => {
    return value.replace(/,/, ".");
  };

  const validateValue = (value: string): React.ReactText => {
    const valueWithoutCommas = replaceCommas(value);
    if (!isNaN(Number(valueWithoutCommas)) && value !== "") {
      const parsedValue = parseFloat(valueWithoutCommas).toFixed(2);
      return parsedValue;
    }
    return "0.00";
  };

  const replaceHistory = (pathname: string = ""): void => {
    navigate({ search: pathname }, { replace: true });
  };

  const bridStatus = window?.APP_SETTINGS?.bridStatus || {};
  const step = (bridStatus.paymentSuccess ? 2 : 1);

  return (
    <Modal>
      {step !== 2 ? (
        <>
          <PaymentNavbar>
            <>
              <BackButton
                type="button"
                onClick={() => props.setShowModal(false)}
              >
                <img src={ArrowLeftImage} />
                {width < 996 ? "Powrót" : "Powrót do strony o projekcie"}
              </BackButton>
              <StyledBridLogo
                className="payment-brid-logo"
                isDarkMode={isDarkMode}
              />
            </>
          </PaymentNavbar>

          <PaymentWizard className="payment-wizard" ref={ref}>
            {width > 996 || (
              <Stepper className={bridStatus.paymentSuccess && "stepper-success"}>
                <PersonIcon />
                <span />
                <BridCheckmark />
              </Stepper>
            )}
            <WrapperColumns>
              <LeftColumn>
                <Box>
                  <>
                    <StepTitle className={h3()}>
                      <span>1. </span>Wybierz kwotę
                    </StepTitle>
                    <BridDonate isBridPaymentModalOpen={true} isSummary={true} {...props} />
                  </>
                </Box>
                <Box>
                  <>
                    <StepTitle className={h3()}>
                      <span>2. </span>Uzupełnij dane wpłacającego
                    </StepTitle>
                    <WrapperBridInput className={bodyLarge()}>
                      <Label>Imię i nazwisko</Label>
                      <Field
                        name="name"
                        type="input"
                        placeholder="Imię i nazwisko"
                        error={props.errors.name?.toString()}
                        touched={props.touched.name?.toString()}
                        className={props.errors.name && props.touched.name && "show-error-input-border"}
                      />
                      <BridTick className={(!props.errors.name && props.touched.name) ? "tick-display" : ""} />
                      <ErrorMessageWrapper>
                        <ErrorMessage name="name" />
                      </ErrorMessageWrapper>
                    </WrapperBridInput>
                    <WrapperBridInput className={bodyLarge()}>
                      <Label>Adres e-mail</Label>
                      <Field
                        name="email"
                        type="input"
                        placeholder="Adres e-mail"
                        error={props.errors.email?.toString()}
                        touched={props.touched.email?.toString()}
                        className={props.errors.email && props.touched.email && "show-error-input-border"}
                      />
                      <BridTick className={(!props.errors.name && props.touched.name) ? "tick-display" : ""} />
                      <ErrorMessageWrapper>
                        <ErrorMessage name="email" />
                      </ErrorMessageWrapper>
                    </WrapperBridInput>
                    <RadioWrap className={bodySmall()}>
                      <RadioTile>
                        <Field
                          id="brid_newsletter"
                          name="newsletter"
                          type="checkbox"
                          checked={props.values.newsletter}
                        />
                        <RadioButton htmlFor="brid_newsletter" />
                      </RadioTile>
                      <span>
                        <span style={{ color: "red" }}> *</span>
                        Zapisz się do newslettera naTemat BRID. Dzięki temu zawsze będziesz wiedzieć, co dzieje się z materiałem, który wspierasz
                      </span>
                    </RadioWrap>
                    <RadioWrap className={bodySmall()}>
                      <RadioTile>
                        <Field
                          id="payment_dataProcessing"
                          name="dataProcessing"
                          type="checkbox"
                          checked={props.values.dataProcessing}
                        />
                        <RadioButton htmlFor="payment_dataProcessing" />
                      </RadioTile>
                      <span>
                        <span style={{ color: "red" }}> *</span>
                        Zapoznałem/am się z
                        {" "}
                        <a href="//natemat.pl/info/regulamin#t4" target="_blank">Regulaminem projektu crowdfundingowego BRID</a>
                        {" "}
                        oraz z{" "}
                        <a
                          href="https://www.przelewy24.pl/obowiazek-informacyjny-platnik"
                          target="_blank"
                        >
                          Obowiązkiem informacyjnym RODO
                        </a>
                      </span>
                    </RadioWrap>
                    <RequiredInfo className={bodySmall()}>
                      <span>*</span> pole obowiązkowe
                    </RequiredInfo>
                  </>
                </Box>
              </LeftColumn>
              <RightColumn>
                {ArticleStore.article.brid_donation_progress && (
                  <ProjectDetails>
                    <BridImage>
                      <Media
                        src={ArticleStore.article.picture.img}
                        preserveRatio={false}
                        aspectRatio={1.5}
                        width={408}
                      />
                    </BridImage>
                    <WrapperContent>
                      <ArticleTitle className={h3()}>
                        {ArticleStore.article.title}
                      </ArticleTitle>
                      <BridDonationProgress
                        blockData={ArticleStore.article.brid_donation_progress}
                        partner={ArticleStore.article.brid_partner || undefined} //może być nullem w starszych artykułach
                        isSummary
                        isBridPaymentModalOpen={true}
                      />
                    </WrapperContent>
                  </ProjectDetails>
                )}
              </RightColumn>
            </WrapperColumns>
            <BottomTile>
              <GoToPayment
                type="submit"
                disabled={!props.isValid || props.waiting}
              >
                {props.waiting ? "Czekaj..." : "Przejdź do płatności"}
              </GoToPayment>
              <SafeInfo>
                <PadlogIcon /> <p>Bezpieczne płatności online</p>
              </SafeInfo>
              {/* onClick={() => replaceHistory('?paymentSuccess=true')} */}
              {/* </div> */}
            </BottomTile>
            {/* </AnimateWrap> */}
            {/* </CSSTransition> */}
          </PaymentWizard>
        </>
      ) : (
        <CSSTransition
          in={bridStatus.paymentSuccess}
          key="second"
          timeout={500}
          classNames="item"
          mountOnEnter
          unmountOnExit
        >
          <AnimateWrap>
            <PaymentNavbar>
              <>
                <BackButton
                  type="button"
                  onClick={() => {
                    replaceHistory("");
                    window.APP_SETTINGS.bridStatus = null;
                    props.setShowModal(false);
                  }}
                >
                  <img src={ArrowLeftImage} />
                  {width < 996 ? "Powrót" : "Powrót do strony o projekcie"}
                </BackButton>
                <StyledBridLogo className="payment-brid-logo" />
              </>
            </PaymentNavbar>
            {width > 996 || (
              <Stepper className={bridStatus.paymentSuccess && "stepper-success"}>
                <PersonIcon />
                <span />
                <BridCheckmark />
              </Stepper>
            )}
            <WrapperMsgSuccess>
              <Header className={h1()}>
                <span>{bridStatus.Name},</span> Dziękujemy za wpłatę!
              </Header>
              <p className={bodyLarge()}>
                Transakcja przebiegła pomyślnie. Informacje o przebiegu zbiórki będą
                wysyłane na <strong>{bridStatus.Mail}</strong>. Jako sponsor
                projektu otrzymasz dostęp do materiału wcześniej niż inni!
              </p>
              <FooterMsg className={bodyLarge()}>
                Wyczekuj wiadomości!<span>Ekipa NaTemat</span>
              </FooterMsg>
              <CloseButton
                type="button"
                onClick={() => {
                  replaceHistory("");
                  window.APP_SETTINGS.bridStatus = null;
                  props.setShowModal(false);
                }}
              >
                Przejdź do artukułu
              </CloseButton>
            </WrapperMsgSuccess>
            <PaymentSuccess />
          </AnimateWrap>
        </CSSTransition>
      )}
    </Modal>
  );
});

// REFACTORING CODE


const ProjectDetails = styled("div", {
  width: "100%",
  minHeight: "625px",
  backgroundColor: "$grayscale100Fixed",
  "@bp4": {
    width: "408px",
  }
});

const BridImage = styled("div", {
  height: "272px",
  width: "100%",
  overflow: "hidden",
});

const ArticleTitle = styled("div", {
  color: "$grayscale0Fixed",
  paddingBottom: "24px",
  marginBottom: "8px",
  borderBottom: "1px solid #FFFFFF1A"
});

const WrapperContent = styled("div", {
  position: "relative",
  padding: "24px",
  backgroundColor: "$grayscale100Fixed",
  "& .brid-donation-progress": {
    padding: 0,
    backgroundColor: "$grayscale100Fixed",
    boxShadow: "none",
    "& .status": {
      paddingTop: "24px",
      marginTop: "24px",
      borderTop: "1px solid #FFFFFF1A",
      fontWeight: "600",
    },
    "& .heading-amount": {
      "& h4": {
        margin: 0,
        color: "$grayscale75",
      }
    }
  }
});

const WrapperColumns = styled("div", {
  display: "flex",
  flexDirection: "column",
  "@bp4": {
    flexDirection: "row",
  }
});

const LeftColumn = styled("div", {
  width: "100%",
  order: 2,
  "@bp4": {
    width: "66%",
    order: 1
  },
  "& .brid-donate": {
    color: "$grayscale100",
    "& label": {
      "& span": {
        borderRadius: "0px",
        border: "2px solid $grayscale25",
        color: "$grayscale100",
        "&:hover": {
          backgroundColor: "$grayscale25 !important",
        }
      },
    },
    "& .brid-donate-own": {
      fontSize: "14px",
      fontWeight: "600",
      lineHeight: "18px",
      "& input": {
        fontSize: "16px",
        fontWeight: "600",
        lineHeight: "24px",
        letterSpacing: "0.2px",
        color: "$grayscale100",
        marginTop: "4px",
      }
    }
  },
  "& .brid-donate-summary": {
    fontSize: "14px",
    lineHeight: "18px",
    fontWeight: "600",
  }
});

const RightColumn = styled("div", {
  width: "100%",
  order: 1,
  marginBottom: "16px",
  "@bp4": {
    marginBottom: "0px",
    width: "33%",
    minWidth: "408px",
    order: 2,
  }
});

const SafeInfo = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& p": {
    marginLeft: "11px",
    color: "$grayscale75",
  }
});

const StepTitle = styled("div", {
  color: "$grayscale100",
  "& span": {
    color: "$primary",
  }
});

const Box = styled("div", {
  backgroundColor: "$grayscale0",
  padding: "16px 24px",
  marginBottom: "16px",
  "@bp4": {
    padding: "64px",
    marginRight: "16px",
  }
});

const StyledBridLogo = styled("div", {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "140px",
  height: "28px",
  backgroundImage: `url("${BridLogo}")`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  variants: {
    isDarkMode: {
      true: {
        backgroundImage: `url("${BridLogoOrange}")`,
      }
    }
  }
});

const PaymentNavbar = styled("div", {
  position: "absolute",
  display: "flex",
  alignItems: "center",
  width: "100%",
  top: 0,
  left: 0,
  height: "56px",
  zIndex: 10000,
  backgroundColor: "$grayscale0",
  boxShadow: "0 1px 2px 0 rgba(35, 44, 72, 0.06)",
  "@bp4": {
    height: "88px",
  }
});

const BackButton = styled("button", {
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  cursor: "pointer",
  paddingLeft: "16px",
  border: 0,
  outline: 0,
  color: "$primary",
  backgroundColor: "transparent",
  fontFamily: "HK Grotesk",
  fontSize: "14px",
  fontWeight: "600",
  lineHeight: "28px",
  "@bp4": {
    paddingLeft: "32px",
    fontSize: "18px",
    lineHeight: "24px",
    letterSpacing: "0.2px",
  },
  "& img": {
    width: "15px",
    height: "15px",
    marginRight: "9px",
  }
});

const RequiredInfo = styled("div", {
  "& span": {
    color: "$primary",
  },
  padding: "20px 0 0 0",
  color: "$grayscale100",
});

const ErrorMessageWrapper = styled("div", {
  color: "$aszdziennikPrimary",
});

const Stepper = styled("div", {
  margin: "15px auto",
  width: "85px",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  "& svg": {
    width: "27px",
    height: "27px",
    "&:nth-of-type(1)": {
      "& path": {
        fill: "$grayscale100",
      }
    },
    "&:nth-of-type(2)": {
      "& path, polyline": {
        stroke: "#d8d8d8",
      }
    }
  },
  "& div": {
    display: "inline-block",
    width: "20px",
    height: "20px",
    border: "1px solid $grayscale100",
    borderRadius: "50%",
  },
  "& span": {
    display: "inline-block",
    height: "1px",
    width: "30px",
    borderBottom: "1px dashed $grayscale100",
  }
});

const CloseButton = styled("button", {
  border: 0,
  outline: "none",
  color: "$grayscale0",
  padding: "10px 20px",
  backgroundColor: "$primary",
  margin: "0 auto"
});

const PaymentWizard = styled("div", {
  margin: 0,
  width: "100%",
  height: "100%",
  overflowY: "auto",
  overflowX: "hidden",
  padding: "56px 0 0 0",
  "& h4": {
    margin: "0 30px",
    paddingTop: "30px",
    paddingBottom: "20px",
  },
  "@bp4": {
    padding: "144px 60px 0px 60px",
    width: "100%",
    height: "100vh",
    zIndex: "6000000",
    transition: "max-width 200ms",
  },
});

const Modal = styled("div", {
  position: "fixed",
  width: "100vw",
  height: "100vh",
  overflow: "auto",
  overscrollBehavior: "contain",
  top: 0,
  left: 0,
  zIndex: "99",
  backgroundColor: "$grayscale10",
  // animacja wjazdu płatności
  /*"& .modal-enter": {
    opacity: 0,
    "& .payment-wizard": {
      transform: "translateY(-100 %)",
      backgroundColor: "red",
    }
  },
  "& .modal-enter-active": {
    opacity: 1,
    transition: "opacity 300ms",
    "& .payment-wizard": {
      transform: "translateY(0)",
      transition: "transform 300ms",
      backgroundColor: "blue",
    }
  },

  "& .modal-exit": {
    opacity: 1,
    "& .payment-wizard": {
      transform: "translateY(0)",
      backgroundColor: "green",
    }
  },

  "& .modal-exit-active": {
    opacity: 0,
    transition: "opacity 300ms",
    "& .payment-wizard": {
      transform: "translateY(-100 %)",
      transition: "transform 300ms",
      backgroundColor: "yellow",
    }
  },
  */
  ".stepper-success": {
    marginTop: "69px",
    "& svg": {
      "&:nth-of-type(2)": {
        "& path, polyline": {
          stroke: "$primary",
        }
      }
    },
  },
  "& .similar-projects-wrap": {
    backgroundColor: "$grayscale10",
  },
  "& .payment-success-wrapper-projects": {
    "& a": {
      color: "$grayscale100Fixed"
    }
  }
});

const AnimateWrap = styled("div", {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  "& .item-enter": {
    opacity: 0,
  },

  "& .item-enter-active": {
    opacity: 1,
    transition: "opacity 400ms",
  },

  "&.item-exit": {
    opacity: 1,
  },

  "& .item-exit-active": {
    opacity: 0,
    transition: "opacity 400ms",
  },
});

const RadioWrap = styled("div", {
  display: "flex",
  flexDirection: "row",
  alignItems: "baseline",
  "& span": {
    fontSize: "12px",
    lineHeight: "16px",
    marginRight: "2px",
    maxWidth: "85%",
  },
  "& span, a": {
    color: "$grayscale100",
  },
  "@pb4": {
    "& span": {
      marginRight: 0,
    }
  },
});


const RadioButton = styled("label", {
  position: "absolute",
  top: "20px",
  left: 0,
  height: "20px",
  width: "20px",
  backgroundColor: "#eee",
  border: "1px solid #7373733d",
  cursor: "pointer",
  transition: "border-color 500ms",
  "&:after": {
    content: "",
    position: "absolute",
    display: "none",
  },
});

const RadioTile = styled("div", {
  position: "relative",
  padding: "18px 0",
  marginRight: "30px",
  "& input": {
    display: "none",
    "&:hover+label": {
      backgroundColor: "#e1e1e1",
    },
    "&:checked+label": {
      "&:after": {
        display: "block",
        top: "0px",
        left: "0px",
        width: "100%",
        height: "100%",
        background: "$primary",
        backgroundImage: `url("${ActiveCheckbox}")`,
      }
    }
  }
});

const WrapperBridInput = styled("div", {
  position: "relative",
  marginTop: "30px",
  "& svg": {
    display: "none",
    position: "absolute",
    right: "4%",
    top: "20px",
    bottom: "20px",
    fill: "$primary",
    backgroundSize: "13px 13px",
    width: "13px",
    height: "13px",
    "@bp4": {
      right: "8%",
    }
  },
  "& .tick-display": {
    display: "block",
  },
  "& p": {
    fontSize: "14px",
    color: "$grayscale100",
  },
  "& input": {
    display: "block",
    padding: "13px 16px",
    width: "90%",
    border: "solid 2px #d8d8d8",
    transition: "border-color 500ms",
    "&::placeholder": {
      color: "#b7b7b7",
    },
    "&:focus": {
      borderColor: "$primary",
    },
    "@bp4": {
      margin: "5px 0px",
    }
  },
  "& .show-error-input-border": {
    border: "2px solid $aszdziennikPrimary",
  }
});

const Label = styled("p", {
  lineHeight: "10px",
  margin: "10px 0",
});

const GoToPayment = styled("button", {
  outline: 0,
  border: "solid 1px $primary",
  color: "#fff",
  backgroundColor: "$primary",
  padding: "11px 20px",
  fontSize: "14px",
  fontWeight: "500",
  textTransform: "uppercase",
  transition: "all 500ms",
  "&:disabled": {
    backgroundColor: "#e7e7e7",
    borderColor: "#e7e7e7",
    color: "#8d8d8d",
  },
  "@bp4": {
    marginRight: "28px",
    marginBottom: "0",
  }
});

const BottomTile = styled("div", {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  whiteSpace: "nowrap",
  marginBottom: "20px",
  "& span": {
    fontSize: "14px",
    color: "#b7b7b7",
    "& b": {
      fontWeight: "normal",
      marginLeft: "10px",
      fontSize: "16px",
      color: "$primary",
    }
  },
  "& div": {
    width: "100%",
  },
  "@bp4": {
    flexDirection: "row",
    padding: "30px",
    paddingLeft: 0,
    marginBottom: "30px",
    "& div": {
      width: "auto",
    },
  }
});

// wiadomość po wpłacie


const WrapperMsgSuccess = styled("div", {
  padding: "40px 16px",
  position: "relative",
  background: "$grayscale0",
  color: "$grayscale100",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  "&::before, &::after": {
    content: "",
    backgroundImage: `url("${ArrowIcons}")`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    display: "block",
    position: "absolute",
    width: "100px",
    height: "200px",
  },
  "&::before": {
    left: "5%",
    top: "-50px",
    transform: "rotate(225deg)",
  },
  "&::after": {
    right: "5%",
    bottom: "-70px",
    transform: "rotate(45deg)",
  },
  "@bp4": {
    marginTop: "88px",
    "&::before": {
      top: "50%",
      left: "5%",
      transform: "translateY(-50%) rotate(180deg)",
      width: "160px",
      height: "220px",
    },
    "&::after": {
      top: "50%",
      right: "5%",
      transform: "translateY(-50%)",
      width: "160px",
      height: "220px",
    }
  },
  "@bp5": {
    "&::before": {
      left: "15%",
    },
    "&::after": {
      right: "15%",
    }
  },
  "& p": {
    maxWidth: "540px",
    margin: "0 auto",
    textAlign: "center",
  }
});

const Header = styled("h1", {
  textAlign: "center",
  "& span": {
    display: "block",
    color: "$primary",
  }
});

const FooterMsg = styled("p", {
  textAlign: "center",
  fontWeight: "800",
  margin: "24px auto !important",
  "@bp4": {
    margin: "32px auto !important"
  },
  "& span": {
    display: "block",
    color: "$primary",
  }
});

/* 

const ProjectDetails = styled.div`
  width: 100%;
  height: 686px;
  background-color: #000;
  @media (min-width: 996px) {
    width: 408px;
  }
`;
const BridImage = styled.div`
  height: 272px;
  width: 100%;
  overflow: hidden;
`;
const ArticleTitle = styled(S.H3)`
  font-size: 24px;
`;
const WrapperContent = styled.div`
  position: relative;
  padding: 24px;
`;
const WrapperColumns = styled.div`
  display: flex;
  flex-direction: column;
  @media (min-width: 996px) {
    flex-direction: row;
  }
`;
const LeftColumn = styled.div`
  width: 100%;
  order: 2;
  @media (min-width: 996px) {
    width: 66%;
    order: 1;
  }
`;
const RightColumn = styled.div`
  width: 100%;
  order: 1;
  @media (min-width: 996px) {
    width: 33%;
    min-width: 408px;
    order: 2;
  }
`;
const SafeInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  > p {
    margin-left: 11px;
  }
`;
const StepTitle = styled(S.H3)`
  margin-bottom: 32px;
  font-size: 24px;
  > span {
    color: #ff6400;
  }
`;
const Box = styled.div`
  background-color: #fff;
  padding: 16px 24px;
  margin-bottom: 16px;
  @media (min-width: 996px) {
    padding: 64px;
    margin-right: 16px;
  }
`;
const StyledBridLogo = styled(BridLogo)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 140px;
`;
const PaymentNavbar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 88px;
  background-color: #fff;
  box-shadow: 0 1px 2px 0 rgba(35, 44, 72, 0.06);
`;

const BackButton = styled.button`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  line-height: 88px;
  padding-left: 16px;
  border: 0;
  outline: 0;
  color: #ff6400;
  background-color: transparent;
  font-family: HK Grotesk;
  font-size: 14px;
  font-weight: 600;
  line-height: 28px;
  @media (min-width: 996px) {
    padding-left: 32px;
    font-size: 18px;
    line-height: 24px;
    letter-spacing: 0.2px;
  }
  & > img {
    width: 15px;
    height: 15px;
    margin-right: 9px;
  }
`;

const RequiredInfo = styled.div`
  span {
    color: red;
  }
  padding: 10px 0 0 10px;
  color: #737373;
  font-size: 11px;
`;

const Stepper = styled.div`
  margin: 15px auto 0 auto;
  width: 85px;
  display: flex;
  flex-direction: row;
  align-items: center;
  & > svg {
    width: 27px;
    height: 27px;
    &:nth-of-type(2) {
      path,
      polyline {
        stroke: ${(props: any) =>
    props.step === 2 ? props.theme.mainColor : "#d8d8d8"};
      }
    }
  }
  & > div {
    display: inline-block;
    width: 20px;
    height: 20px;
    border: 1px solid black;
    border-radius: 50%;
  }
  & > span {
    display: inline-block;
    height: 1px;
    width: 30px;
    border-bottom: 1px dashed black;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 500px;
  left: 50%;
  transform: translateX(-50%);
  border: 0;
  outline: none;
  color: #fff;
  z-index: 5000001;
  padding: 10px 20px;
  background-color: #FF6400;
  @media (min-width: 768px) {
    top: 500px;
  };
`;

const PaymentWizard = styled.div`

  margin: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  h4 {
    margin: 0 30px;
    padding-top: 30px;
    padding-bottom: 20px;
  }

  @media (min-width: 996px) {
    padding: 60px;
    width: 100%;
    height: 100vh;
    z-index: 6000000;
    transition: max-width 200ms;
  }
`;

const Modal = styled.div<{ step: number }>`
  position: fixed;
  width: 100vw;
  height: 100vh;
  overflow: auto;
  overscroll-behavior: contain;
  top: 0;
  left: 0;
  z-index: 99;
  background-color: ${({ step }) => (step === 2 ? "#fff" : "#e5e5e5")};
  &.modal-enter {
    opacity: 0;
    & > ${PaymentWizard} {
      transform: translateY(-100%);
    }
  }

  &.modal-enter-active {
    opacity: 1;
    transition: opacity 300ms;
    & > ${PaymentWizard} {
      transform: translateY(0);
      transition: transform 300ms;
    }
  }

  &.modal-exit {
    opacity: 1;
    & > ${PaymentWizard} {
      transform: translateY(0);
    }
  }

  &.modal-exit-active {
    opacity: 0;
    transition: opacity 300ms;
    & > ${PaymentWizard} {
      transform: translateY(-100%);
      transition: transform 300ms;
    }
  }
`;
const AnimateWrap = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  &.item-enter {
    opacity: 0;
  }

  &.item-enter-active {
    opacity: 1;
    transition: opacity 400ms;
  }

  &.item-exit {
    opacity: 1;
  }

  &.item-exit-active {
    opacity: 0;
    transition: opacity 400ms;
  }
`;

const RadioWrap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  span {
    font-family: sans-serif;
    font-size: 14px;
    line-height: 1.43;
    margin-right: 2px;
  }
  span,
  a {
    color: #737373;
  }
  @media (min-width: 996px) {
    span {
      margin-right: 0;
    }
  }
`;

const TileWrap = styled(RadioWrap)`
  align-items: center;
  text-align: center;
  flex-direction: column;
  img {
    float: left;
    margin-left: 20px;
  }
  span {
    font-size: 16px;
    color: #272424;
    font-weight: 500;
    float: left;
    margin: 25px 20px;
    font-weight: 500;
  }
  @media (min-width: 996px) {
    flex-direction: row;
    img {
      margin: 0;
      float: none;
    }
    span {
      font-size: 12px;
      font-weight: 300;
      text-align: center;
      margin: 0;
      float: none;
    }
  }
`;

const RadioButton = styled.label`
  position: absolute;
  top: 20px;
  left: 0;
  height: 20px;
  width: 20px;
  background-color: #eee;
  border: 1px solid #7373733d;
  cursor: pointer;
  transition: border-color 500ms;
  &:after {
    content: "";
    position: absolute;
    display: none;
  }
`;

const RadioTile = styled.div<{ checkIcon: string }>`
  position: relative;
  padding: 18px 0;
  margin-right: 30px;
  input {
    display: none;
    &:hover + label {
      background-color: #e1e1e1;
    }

    &:checked + label {
      &:after {
        display: block;
        top: 0px;
        left: 0px;
        width: 100%;
        height: 100%;
        background: #ff6e00;
        background-image: url(${(props) => props.checkIcon});
      }
    }
  }
`;
const WrapperBridInput = styled.div`
  position: relative;
  margin-top: 30px;
`;
const Label = styled.p`
  line-height: 10px;
  margin: 10px 0;
`;
const BridInput = styled(Field)`
  display: block;
  padding: 13px 20px;
  width: 90%;
  border: solid 2px #d8d8d8;
  font-size: 16px;
  border-color: ${(props: any) => !props.error && props.touched && "black"};
  transition: border-color 500ms;
  &::placeholder {
    color: #b7b7b7;
  }
  &:focus {
    border-color: ${(props: any) => props.theme.mainColor};
  }
  & + svg {
    display: ${(props: any) =>
    !props.error && props.touched ? "block" : "none"};
    position: absolute;
    right: 8%;
    top: 20px;
    bottom: 20px;
    fill: ${(props: any) => props.theme.mainColor};
    background-size: 13px 13px;
    width: 13px;
    height: 13px;
  }

  @media (min-width: 996px) {
    margin: 5px 0px;
  }
`;

const GoToPayment = styled.button`
  outline: 0;
  background-color: #ffffff;
  border: solid 1px ${(props: any) => props.theme.mainColor};
  color: #fff;
  background-color: ${(props: any) => props.theme.mainColor};
  padding: 11px 20px;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  transition: all 500ms;
  &:disabled {
    background-color: #e7e7e7;
    border-color: #e7e7e7;
    color: #8d8d8d;
  }
  @media (min-width: 996px) {
    margin-right: 28px;
    margin-bottom: 0;
  }
`;

const BottomTile = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  white-space: nowrap;
  margin-bottom: 100px;
  span {
    font-size: 14px;
    color: #b7b7b7;
    b {
      font-weight: normal;
      margin-left: 10px;
      font-size: 16px;
      color: ${(props: any) => props.theme.mainColor};
    }
  }
  & > div {
    width: 100%;
  }
  @media (min-width: 996px) {
    flex-direction: row;
    padding: 30px;
    padding-left: 0;
    margin-bottom: 30px;
    & > div {
      width: auto;
    }
  }
`;

const PaymentTitle = styled.div`
  margin: 0 46px 0 30px;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 16px;
  p {
    font-size: 16px;
    font-weight: normal;
    color: #272424;
    flex-grow: 1;
  }
  span {
    display: block;
    margin-left: auto;
    text-align: right;
    color: ${(props: any) => props.theme.mainColor};
  }

  svg {
    right: 16px !important;
    top: 25px !important;
  }

  @media (min-width: 996px) {
    p {
      margin-left: 15px;
      font-weight: 500;
      color: #141821;
    }
  }
`;

const Cancel = styled.button`
  border: 0;
  outline: 0;
  background-color: #ffffff;
  font-family: "HK Grotesk", sans-serif;
  font-size: 14px;
  line-height: 1.5;
  letter-spacing: normal;
  color: #333333;
`;

*/

export default Payment;
